import { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Sun, Moon, Globe, Menu, X } from 'lucide-react';

export const Navbar = () => {
  const { isDark, toggleTheme } = useTheme();
  const { language, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'navbar-scrolled py-3' : 'py-5 bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a
            href="#hero"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection('hero');
            }}
            data-testid="logo"
            className="text-xl md:text-2xl font-bold text-foreground hover:text-primary transition-colors"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            NordensWebStudio
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <button
              onClick={() => scrollToSection('services')}
              data-testid="nav-services"
              className={`transition-colors font-medium ${
                isDark 
                  ? 'text-muted-foreground hover:text-foreground' 
                  : isScrolled 
                    ? 'text-slate-700 hover:text-primary' 
                    : 'text-white/90 hover:text-white'
              }`}
            >
              {t('services')}
            </button>
            <button
              onClick={() => scrollToSection('portfolio')}
              data-testid="nav-portfolio"
              className={`transition-colors font-medium ${
                isDark 
                  ? 'text-muted-foreground hover:text-foreground' 
                  : isScrolled 
                    ? 'text-slate-700 hover:text-primary' 
                    : 'text-white/90 hover:text-white'
              }`}
            >
              {t('portfolio')}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              data-testid="nav-contact"
              className={`transition-colors font-medium ${
                isDark 
                  ? 'text-muted-foreground hover:text-foreground' 
                  : isScrolled 
                    ? 'text-slate-700 hover:text-primary' 
                    : 'text-white/90 hover:text-white'
              }`}
            >
              {t('contact')}
            </button>

            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              data-testid="language-toggle"
              className="flex items-center gap-2 px-3 py-2 rounded-full glass hover:border-primary/30 transition-all"
            >
              <Globe size={18} />
              <span className="text-sm font-medium uppercase">{language}</span>
            </button>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className="p-2 rounded-full glass hover:border-primary/30 transition-all"
              aria-label="Toggle theme"
            >
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-button"
            className="md:hidden p-2 rounded-lg glass"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            data-testid="mobile-menu"
            className="md:hidden mt-4 p-6 rounded-2xl glass fade-in-up"
          >
            <div className="flex flex-col gap-4">
              <button
                onClick={() => scrollToSection('services')}
                data-testid="mobile-nav-services"
                className="text-left text-lg text-muted-foreground hover:text-foreground transition-colors"
              >
                {t('services')}
              </button>
              <button
                onClick={() => scrollToSection('portfolio')}
                data-testid="mobile-nav-portfolio"
                className="text-left text-lg text-muted-foreground hover:text-foreground transition-colors"
              >
                {t('portfolio')}
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                data-testid="mobile-nav-contact"
                className="text-left text-lg text-muted-foreground hover:text-foreground transition-colors"
              >
                {t('contact')}
              </button>
              
              <div className="flex items-center gap-4 pt-4 border-t border-border">
                <button
                  onClick={toggleLanguage}
                  data-testid="mobile-language-toggle"
                  className="flex items-center gap-2 px-4 py-2 rounded-full glass"
                >
                  <Globe size={18} />
                  <span className="text-sm font-medium uppercase">{language}</span>
                </button>
                <button
                  onClick={toggleTheme}
                  data-testid="mobile-theme-toggle"
                  className="p-2 rounded-full glass"
                >
                  {isDark ? <Sun size={20} /> : <Moon size={20} />}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
